// TYPES
import { useMemo } from 'react';
// Slices
import { useSelector } from 'react-redux';
import HistoryTime from 'components/HistoryTime';
import _ from 'lodash';
import { authconfigByIdSelector } from 'slices/authConfig.slice';
import { dateFormatsSelector, languagesSelector } from 'slices/language.slice';
import type { TRootState } from 'slices/store';
import { timeZonesSelector } from 'slices/timeZone.slice';
import { EAppId, EUserVisibility } from '@timeedit/types/lib/enums';
import {
  EAuthStrategy,
  type ITEUserWithTEIdPAuthSettings,
  type TLanguage,
} from '@timeedit/types/lib/types';
// COMPONENTS
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Space, Typography } from '@timeedit/ui-components/antd';
import { PRODUCTS_MAP } from '@timeedit/ui-components/components/ProductSelector/_static';
import HistoryDetail from '../../../../components/HistoryTime/HistoryDetail';
// STYLES
import './General.scss';

type GeneralProps = {
  user: ITEUserWithTEIdPAuthSettings;
  onChange: (key: string, value: any) => void;
};

export const General = ({ user, onChange }: GeneralProps) => {
  const allLanguages = useSelector(languagesSelector);
  const allTimezones = useSelector(timeZonesSelector);
  const dateFormats = useSelector(dateFormatsSelector);
  const authConfig = useSelector((state: TRootState) =>
    authconfigByIdSelector(state, user?.authConfigId)
  );

  const langList = allLanguages
    .filter((lang: TLanguage) => lang.isActive)
    .map((lang: TLanguage) => ({
      value: lang.id,
      label: lang.name,
    }));

  const dateFormatOptions = dateFormats.map((format: TLanguage) => ({
    value: format.id,
    label: format.name,
  }));

  const loginHistory = useMemo(
    () =>
      user?.logins?.map((login) => ({
        time: login.login,
      })) ?? [],
    [user?.logins]
  );

  const handleChange = (key: string, value: any) => {
    // If discard is triggered by the <Esc> key, the change will be triggered
    // after the user drawer object has been removed. This prevents an update
    // that will make the drawer object an object with only the changed property
    if (user) {
      onChange(key, value);
    }
  };

  return (
    <TEDrawerSection label="General">
      <div className="user-general-section">
        <div className="user-general-section-column">
          <TEInput.Input
            value={user?.firstName as string}
            label="First name"
            onChange={(e) => handleChange('firstName', e)}
            tooltip="First name"
          />
          <TEInput.Input
            value={user?.lastName as string}
            label="Last name"
            onChange={(e) => handleChange('lastName', e)}
            tooltip="Last name"
          />
          <TEInput.TextArea
            value={user?.description as string}
            label="Description"
            onChange={(e) => handleChange('description', e)}
            tooltip="Description"
          />
        </div>
        <div className="user-general-section-column">
          <TEInput.Input
            value={user?.email}
            label="Email"
            onChange={(e) => handleChange('email', e)}
            tooltip="Email"
          />
          <TEInput.Input
            label="Username"
            tooltip="Username"
            value={user?.username}
            onChange={(e) => handleChange('username', e)}
          />
          <TEInput.Input
            value={user?.recoveryEmail}
            label="Recovery email"
            onChange={(e) => handleChange('recoveryEmail', e)}
            tooltip="Adding a recovery email address will redirect ALL password reset emails to the recovery email address instead of the standard email address of the user. If left empty, password reset emails will be sent to the user email as usual."
          />
          <TEInput.Switch
            label="Contains personal data"
            checked={user?.personalData}
            onChange={(e) => handleChange('personalData', e)}
            tooltip="Contains personal data"
          />
        </div>
        <div className="user-general-section-column">
          <TEInput.Select
            label="Language"
            value={user?.language}
            onChange={(e) => handleChange('language', e)}
            options={langList}
            tooltip="Language"
          />
          <TEInput.Select
            label="Date format"
            value={user?.dateFormat}
            onChange={(e) => handleChange('dateFormat', e)}
            options={dateFormatOptions}
            tooltip="Date format"
          />
          <TEInput.Select
            label="Timezone"
            value={user?.timezone}
            options={allTimezones.map((timezone) => ({
              value: timezone.extId,
              label: timezone.name,
            }))}
            onChange={(e) => handleChange('timezone', e || '')}
            tooltip="Timezone"
          />
          <TEInput.Select
            label="Visibility"
            value={user?.visibility}
            onChange={(e) => handleChange('visibility', e)}
            options={Object.keys(EUserVisibility).map((key) => ({
              value: key,
              label: key,
            }))}
            tooltip="Visibility"
          />
        </div>
        <div className="user-general-section-column">
          <HistoryTime
            history={user?.history}
            createdAt={user?.createdAt}
            createdBy={user?.createdBy}
            isSAML2={authConfig?.strategy === EAuthStrategy.SAML2}
          />
          <Space>
            <HistoryDetail label="Last login" history={loginHistory} />
          </Space>
        </div>
        {/* 

        This will not work until we know where to get the timezones from

        <TEInput
          type="select"
          value={user?.timezone}
          label="Timezone"
          options={[]}
          onChange={(e) => onChange('timezone', e)}
          tooltip="Timezone"
        /> 
        */}
        {/* 

        This will not work until we know where to get the date formats from

        <TEInput
          type="select"
          label="Date format"
          value={user?.dateFormat}
          onChange={(e) => onChange('dateFormat', e)}
          options={[]}
          tooltip="Date format"
        /> 
        */}
      </div>
    </TEDrawerSection>
  );
};
